import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';

interface SkewedButtonProps {
  to?: string;
  onClick?: () => void;
  children: React.ReactNode;
  variant?: 'primary' | 'secondary';
  className?: string;
  type?: 'button' | 'submit' | 'reset';
  variance?: number;
  disabled?: boolean;
}

interface Point {
  x: number;
  y: number;
}

/**
 * A button component with randomly skewed quadrilateral shape using SVG borders.
 * Can be used as a link (with 'to' prop) or button (with 'onClick' prop).
 */
const SkewedButton: React.FC<SkewedButtonProps> = ({
  to,
  onClick,
  children,
  variant = 'primary',
  className = '',
  type = 'button',
  variance = 0.06,
  disabled = false
}) => {
  const [points, setPoints] = useState<Point[]>([]);

  useEffect(() => {
    // Generate random offset within variance range
    const getRandomOffset = () => {
      return (Math.random() - 0.5) * variance * 100;
    };

    // Generate four corners with slight random variations
    const corners = [
      { x: 0 + getRandomOffset(), y: 0 + getRandomOffset() }, // top-left
      { x: 100 + getRandomOffset(), y: 0 + getRandomOffset() }, // top-right
      { x: 100 + getRandomOffset(), y: 100 + getRandomOffset() }, // bottom-right
      { x: 0 + getRandomOffset(), y: 100 + getRandomOffset() } // bottom-left
    ];

    setPoints(corners);
  }, [variance]);

  // Convert percentage points to SVG polygon points string
  const svgPoints = points.map(p => `${p.x},${p.y}`).join(' ');

  const baseStyles = 'relative px-8 py-4 font-semibold transition-all inline-block';
  const variantStyles = variant === 'primary'
    ? 'text-white'
    : 'text-black';
  const disabledStyles = disabled ? 'opacity-50 cursor-not-allowed' : '';

  const combinedStyles = `${baseStyles} ${variantStyles} ${disabledStyles} ${className}`;

  const content = (
    <>
      {/* SVG border overlay */}
      <svg
        className="absolute inset-0 pointer-events-none"
        width="100%"
        height="100%"
        viewBox="0 0 100 100"
        preserveAspectRatio="none"
        style={{ overflow: 'visible' }}
      >
        <polygon
          points={svgPoints}
          fill={variant === 'primary' ? 'black' : 'white'}
          stroke="black"
          strokeWidth={0.4}
          vectorEffect="non-scaling-stroke"
        />
      </svg>
      {/* Text content */}
      <span className="relative z-10">{children}</span>
    </>
  );

  if (to) {
    return (
      <Link
        to={to}
        className={combinedStyles}
      >
        {content}
      </Link>
    );
  }

  return (
    <button
      type={type}
      onClick={onClick}
      className={combinedStyles}
      disabled={disabled}
    >
      {content}
    </button>
  );
};

export default SkewedButton;
